import { DocumentType, modelOptions, PropType, post, pre, prop, type Ref } from "@typegoose/typegoose";
import { Severity } from "@typegoose/typegoose/lib/internal/constants.js";
import mongoose from "mongoose";
import { z } from "zod";
import { EventInstance } from "../../scheduling/eventInstance.js";
import { isValidNum } from "../../utils/mathUtils.js";
import { TimeOverride } from "../../utils/index.js";
import { RRule, rrulestr } from "../../utils/RRuleLibrary.js";
import {
    getZodSchemaFromClass,
    milliseconds,
    ObjectIdString,
    RRuleString,
    validate,
} from "../../zod-decorator/index.js";
import { CommandGroup } from "../commandGroup/commandGroup.js";
import type { IrrigationSet } from "../irrigationSet/irrigationSet.js";
import { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { createDelayedRegistrationHook } from "../utils/delayedRegistrationHook.js";
import type {
    IrrigationEventFieldsWithDefaults as IrrigationEventGeneratedFieldsWithDefaults,
    IrrigationEventSchemaTypes,
} from "../types/irrigationEvent.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { Zone } from "../zone/zone.js";

const SCHEMA_KEY = "IrrigationEvent" as const;

type IrrigationEventFieldsWithDefaults =
    | IrrigationEventGeneratedFieldsWithDefaults
    | "versionId"
    | "versionValidFrom"
    | "deleted"
    | "versionIsCurrent"
    | "versionCreatedAt"
    | "recordCreatedAt";

// Populates the active-dates cache (the `datesActive` document field and the supplemental
// IrrigationEventUpcomingActiveDates collection) after an IrrigationEvent is saved. The DB-writing
// implementation lives in the backend and is registered in registerHooks.ts once the DB is connected.
export const irrigationActiveDatesPostSave = createDelayedRegistrationHook<
    (event: DocumentType<IrrigationEvent>) => Promise<void>
>("irrigationActiveDatesPostSave");

@pre<IrrigationEvent>("save", function saveIrrigation(this: DocumentType<IrrigationEvent>) {
    if (!this.zonesActive) {
        this.zonesActive = [];
    }
    if (
        this.isNew ||
        this.isModified("defaultDuration") ||
        this.isModified("zoneWateringPercentages") ||
        this.isModified("zonesActive") ||
        this.isModified("rrule")
    ) {
        const allKeys = new Set([
            ...this.zonesActive.map((z) => z.toString()),
            ...Object.keys(this.zoneWateringPercentages || {}),
        ]);
        allKeys.forEach((zoneId) => {
            if (!this.zoneWateringPercentages) {
                this.zoneWateringPercentages = {};
            }
            if (!isValidNum(this.zoneWateringPercentages[zoneId])) {
                this.zoneWateringPercentages[zoneId] = 1;
            }
            if (this.zoneWateringPercentages[zoneId] < 0) {
                this.zoneWateringPercentages[zoneId] = 0;
            }
            if (this.zoneWateringPercentages[zoneId] > 3) {
                this.zoneWateringPercentages[zoneId] = 3;
            }
            // Note: Removed toMongoId() call as it's a backend-specific extension method
            if (!this.zonesActive.map((z) => z.toString()).includes(zoneId)) {
                this.zonesActive.push(zoneId as any);
            }
        });
    }

    // Stamp SCD Type 2 bookkeeping on every save (new docs + legacy back-stamps). recordId != _id.
    const now = TimeOverride.now();
    if (!this.recordId) this.recordId = new mongoose.Types.ObjectId();
    if (this.versionId == null) this.versionId = 1;
    const createdAt = (this as { createdAt?: Date }).createdAt ?? now;
    if (!this.recordCreatedAt) this.recordCreatedAt = createdAt ?? now;
    if (!this.versionValidFrom) this.versionValidFrom = createdAt ?? this.recordCreatedAt;
    if (!this.versionCreatedAt) this.versionCreatedAt = createdAt ?? now;
    if (this.deleted === undefined) this.deleted = false;
    if (this.versionIsCurrent === undefined) this.versionIsCurrent = true;
})
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "irrigations",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<IrrigationEvent>>(...addSchemaKeyHook(SCHEMA_KEY))
@post<IrrigationEvent>("save", async function refreshActiveDatesCache(this: DocumentType<IrrigationEvent>) {
    await irrigationActiveDatesPostSave.function(this);
})
export class IrrigationEvent extends SchemaBase<IrrigationEvent, IrrigationEventFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public _lastRefreshed?: Date;

    @validate({ zodValidator: z.array(z.string()).optional(), responseOnly: true })
    @prop({ type: () => String, required: true, default: [] }, PropType.ARRAY)
    public datesActive: string[] = [];

    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ ref: () => Zone, required: true, default: [] }, PropType.ARRAY)
    public zonesActive: Ref<Zone>[] = [];

    @validate({ zodValidator: z.record(z.string(), z.number()).optional() })
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: true, default: {} })
    public zoneWateringPercentages: Record<string, number> = {};

    @validate({ zodValidator: z.record(z.string(), z.number()).optional() })
    @prop({ type: () => Object, allowMixed: Severity.ALLOW, required: false })
    public zoneIrrigationOffsets?: Record<string, number>;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ ref: () => Organization, required: true })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: ObjectIdString.optional() })
    @prop({ ref: () => CommandGroup, required: false })
    public commandGroup?: Ref<CommandGroup>;

    @validate({ zodValidator: milliseconds })
    @prop({ type: () => Number, required: true })
    public defaultDuration!: number; // milliseconds

    @validate({ zodValidator: RRuleString })
    @prop({ type: () => String, required: true })
    public rrule!: string;

    @validate({ zodValidator: ObjectIdString.optional() })
    @prop({ type: () => mongoose.Types.ObjectId, ref: "IrrigationSet", required: false })
    public irrigationSet?: Ref<IrrigationSet>;

    // SCD Type 2 bookkeeping (populated ahead of the full SCD migration). Fields with static v1
    // defaults use required-with-default per schemas/README.md; the @pre("save") hook still
    // back-stamps legacy docs and refines timestamps from Mongoose createdAt when present.
    // recordId has no @prop default — minted only in the @pre("save") hook so legacy loads
    // stay undefined until persisted. Independent, version-stable identity (deliberately != _id).
    @prop({ type: () => mongoose.Types.ObjectId, required: false })
    public recordId?: mongoose.Types.ObjectId;

    @prop({ type: () => Number, required: true, default: 1 })
    public versionId: number = 1;

    @prop({ type: () => Date, required: true, default: () => TimeOverride.now() })
    public versionValidFrom: Date = TimeOverride.now();

    @prop({ type: () => Date, required: false })
    public versionValidTo?: Date;

    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    @prop({ type: () => Boolean, required: true, default: true })
    public versionIsCurrent: boolean = true;

    @prop({ type: () => mongoose.Types.ObjectId, required: false })
    public versionEventId?: mongoose.Types.ObjectId;

    @prop({ type: () => Date, required: true, default: () => TimeOverride.now() })
    public versionCreatedAt: Date = TimeOverride.now();

    @prop({ type: () => Date, required: true, default: () => TimeOverride.now() })
    public recordCreatedAt: Date = TimeOverride.now();

    // Getters are functions that take the schema instance and return a value
    static getters = {
        /**
         * Gets the next instance start time for this irrigation event, or `null` when the
         * recurrence has ended (expired COUNT, past UNTIL, etc.).
         */
        getNextInstanceStart: (self: IrrigationEvent): Date | null => {
            const rule = rrulestr(self.rrule);
            const nextOccurrence = rule.after(new Date(TimeOverride.now().getTime() - self.defaultDuration), true);
            if (nextOccurrence === null) {
                return null;
            }
            return new Date(nextOccurrence.getTime());
        },

        /**
         * Gets the maximum duration considering all zone watering percentages and offsets
         */
        getMaximumDuration: (self: IrrigationEvent): number => {
            let longestDuration = self.defaultDuration;
            try {
                const zoneKeys = Object.keys(self.zoneWateringPercentages || {});
                if (zoneKeys.length > 0) {
                    longestDuration = 0;
                    zoneKeys.forEach((zone) => {
                        const percentage = self.zoneWateringPercentages[zone];
                        const endOfInstance =
                            (isValidNum(percentage) ? percentage : 1) * self.defaultDuration +
                            (self.zoneIrrigationOffsets?.[zone] || 0);
                        if (endOfInstance > longestDuration) {
                            longestDuration = endOfInstance;
                        }
                    });
                }
            } catch (e) {
                console.error("Error calculating longest duration for irrigation event", e);
                longestDuration = 1000 * 60 * 60 * 24;
            }
            return longestDuration;
        },
    } as const satisfies Record<string, (self: IrrigationEvent) => any>;

    // Utilities are functions that can take additional parameters beyond the schema instance
    static utilities = {
        /**
         * Gets single commands for a specific zone within a time scope
         */
        getSingleCommands: (self: IrrigationEvent, zone: string, startScope: Date, endScope: Date): EventInstance[] => {
            if (!(self.defaultDuration && self.rrule)) {
                console.error(`ERROR: NO DEFAULT DURATION OR RRULE ON EVENT`);
                return [];
            }
            const eventInstances = Array<EventInstance>();
            const rrule = RRule.fromString(self.rrule);
            const percentage = self.zoneWateringPercentages?.[zone];
            const multiplier = isValidNum(percentage) ? percentage : 1;
            const duration = self.defaultDuration * multiplier;
            const maximumDuration = IrrigationEvent.getters.getMaximumDuration(self);
            const startTimes = rrule.between(new Date(startScope.valueOf() - maximumDuration * 2), endScope);

            let startTimeOffset = 0;
            if (self.zoneIrrigationOffsets && self.zoneIrrigationOffsets[zone]) {
                startTimeOffset = self.zoneIrrigationOffsets[zone];
            }

            startTimes.forEach((startTime) => {
                const newEventInstance = new EventInstance();
                newEventInstance.startDate = new Date(startTime.valueOf() + startTimeOffset);
                newEventInstance.duration = duration / 1000;
                newEventInstance.zone = zone;
                newEventInstance.rootIrrigationID = (self as any)._id?.toString();
                eventInstances.push(newEventInstance);
            });
            return eventInstances;
        },

        // NOTE: The following methods require database access and should be implemented
        // in the backend on the IrrigationEventModel after this schema is integrated:
        //
        // - findByRelevancy(zoneID, date, minHoursInFuture, minHoursInPast)
        //   Finds irrigation events by relevancy to a zone and date
        //
        // - getStateChangesForTimePeriodAndZone(zoneID, date, minHoursInFuture, minHoursInPast, marginToCombineEvents)
        //   Gets a list of eventInstances that have no overlaps and are sorted past to future
        //
        // - getActiveIrrigationAtTime(zoneID, date, minHoursInFuture, minHoursInPast)
        //   Gets the active irrigation event at a specific time
        //
        // - getActiveIrrigationAsFunctionOfTime(zoneID, dateRange, minHoursInFuture, minHoursInPast)
        //   Gets active irrigation as a function of time (returns a function that takes a date)
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<IrrigationEventSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<IrrigationEvent, IrrigationEventFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

IrrigationEvent.dtoValidators = getZodSchemaFromClass(
    IrrigationEvent,
) as unknown as DtoValidatorTypes<IrrigationEventSchemaTypes>;
